import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Col, Divider, Message, Panel, Row, Tag, Toggle } from 'rsuite';
import AdminDashboard from '../../components/AdminDashboard';
import { User } from '../../my-type';
import { State } from '../../state';
import { actionCreators } from '../../state';
import { updateTeamAdminSettings } from '../../service/TeamService';
import { getTeamAdminUserUID, isTeamAdminUser } from '../../util/teamAdmin';

const ROLE_OPTIONS = [
  { label: '乘客', value: 'User' },
  { label: '司機', value: 'Driver' },
  { label: '車隊管理員', value: 'Lead' }
];

const AccountInfoPage = () => {
  const user: User | null = useSelector((state: State) => state.user);
  const dispatch = useDispatch();
  const { updateUser } = bindActionCreators(actionCreators, dispatch);
  const navigate = useNavigate();
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const teamAdminUserUID = getTeamAdminUserUID(user?.team);
  const isTeamAdmin = isTeamAdminUser(user);

  const formattedLastLogin = useMemo(() => {
    if (!user?.lastLogin) return '-';
    const loginDate = user.lastLogin instanceof Date ? user.lastLogin : new Date(user.lastLogin);
    if (Number.isNaN(loginDate.getTime())) return '-';
    return new Intl.DateTimeFormat('zh-TW', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(loginDate);
  }, [user?.lastLogin]);

  const getRoleBadgeColor = (role: string): 'red' | 'orange' | 'yellow' | 'green' | 'cyan' | 'blue' | 'violet' => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'red';
      case 'driver':
        return 'blue';
      case 'lead':
        return 'orange';
      case 'user':
        return 'green';
      default:
        return 'cyan';
    }
  };

  const getRoleLabel = (role: string) => {
    const match = ROLE_OPTIONS.find((option) => option.value.toLowerCase() === role.toLowerCase());
    return match ? match.label : role;
  };

  const onLogout = () => {
    updateUser(null);
    navigate('/login');
  };

  const handleTeamAdminSettingsChange = async (settings: {
    allowDriverLeadApplication?: boolean;
    allowDriverAutoLeadOnAdminLogin?: boolean;
  }) => {
    if (!user?.team?.code || !isTeamAdmin) {
      return;
    }

    setIsSavingSettings(true);
    setSettingsError('');
    setSettingsSuccess('');

    try {
      const updatedTeam = await updateTeamAdminSettings({
        teamCode: user.team.code,
        allowDriverLeadApplication:
          settings.allowDriverLeadApplication ?? !!user.team.allowDriverLeadApplication,
        allowDriverAutoLeadOnAdminLogin:
          settings.allowDriverAutoLeadOnAdminLogin ?? !!user.team.allowDriverAutoLeadOnAdminLogin,
      });

      updateUser({
        ...user,
        team: {
          ...user.team,
          ...updatedTeam,
        },
      });
      setSettingsSuccess('管理設定已更新');
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : '更新管理設定失敗');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleAllowDriverLeadApplicationChange = async (checked: boolean) => {
    await handleTeamAdminSettingsChange({ allowDriverLeadApplication: checked });
  };

  const handleAllowDriverAutoLeadOnAdminLoginChange = async (checked: boolean) => {
    await handleTeamAdminSettingsChange({ allowDriverAutoLeadOnAdminLogin: checked });
  };

  return (
    <AdminDashboard title="帳號資訊">
      {!user && (
        <Message type="warning" style={{ marginBottom: '20px' }}>
          找不到登入使用者資訊，請重新登入。
        </Message>
      )}

      <Panel header="登入身分" bordered className="fleet-account-card">
        <Row gutter={16}>
          <Col md={12}>
            <p><strong>顯示名稱:</strong> {user?.displayName || '-'}({user?.id})</p>
          </Col>
          <Col md={12}>
            <p><strong>登入方式:</strong> {user?.authProvider || '-'}</p>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12}>
            <p><strong>車隊管理員 UID:</strong> {teamAdminUserUID ?? '-'}</p>
          </Col>
          <Col md={12}>
            <p><strong>管理員身分:</strong> {isTeamAdmin ? '是' : '否'}</p>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col md={12}>
            <p style={{ marginBottom: 8 }}><strong>帳號角色:</strong></p>
            <div>
              {user?.roleString
                ? user.roleString
                  .split(',')
                  .map((role) => (
                    <Tag
                      key={role.trim()}
                      color={getRoleBadgeColor(role.trim())}
                      style={{ marginRight: 6, marginBottom: 6 }}
                    >
                      {getRoleLabel(role.trim())}
                    </Tag>
                  ))
                : '-'}
            </div>
          </Col>
          <Col md={12}>
            <p><strong>最後登入:</strong> {formattedLastLogin}</p>
          </Col>
        </Row>

        <Divider />

        <ButtonGroup>
          <Button appearance="primary" color="red" onClick={onLogout}>
            登出
          </Button>
          <Button appearance="subtle" onClick={() => navigate('/')}>
            返回總覽
          </Button>
        </ButtonGroup>
      </Panel>

      {isTeamAdmin && (
        <Panel header="車隊管理權限" bordered style={{ marginTop: 20 }}>
          {settingsError && (
            <Message type="error" style={{ marginBottom: 16 }}>
              {settingsError}
            </Message>
          )}
          {settingsSuccess && (
            <Message type="success" style={{ marginBottom: 16 }}>
              {settingsSuccess}
            </Message>
          )}

          <Row gutter={16}>
            <Col md={18}>
              <p style={{ marginBottom: 4 }}><strong>允許司機申請代理隊長</strong></p>
              <p style={{ color: '#666', marginBottom: 0 }}>
                關閉後，司機在管理 App 登入時不可送出代理隊長申請。
              </p>
            </Col>
            <Col md={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Toggle
                aria-label="允許司機申請代理隊長"
                checked={!!user?.team?.allowDriverLeadApplication}
                checkedChildren="開啟"
                unCheckedChildren="關閉"
                loading={isSavingSettings}
                disabled={isSavingSettings}
                onChange={handleAllowDriverLeadApplicationChange}
              />
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col md={18}>
              <p style={{ marginBottom: 4 }}><strong>司機登入管理 App 時自動成為代理隊長</strong></p>
              <p style={{ color: '#666', marginBottom: 0 }}>
                開啟後，已審核司機首次登入管理 App 時會自動取得代理隊長權限，不需管理員核准。
              </p>
            </Col>
            <Col md={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Toggle
                aria-label="司機登入管理 App 時自動成為代理隊長"
                checked={!!user?.team?.allowDriverAutoLeadOnAdminLogin}
                checkedChildren="開啟"
                unCheckedChildren="關閉"
                loading={isSavingSettings}
                disabled={isSavingSettings}
                onChange={handleAllowDriverAutoLeadOnAdminLoginChange}
              />
            </Col>
          </Row>
        </Panel>
      )}
    </AdminDashboard>
  );
};

export default AccountInfoPage;
